/**
 * Zone Card
 *
 * Displays a single irrigation zone with status and controls.
 */

import React, { useState } from 'react';
import { HydrawiseZone, useHydrawiseStore } from '../hydrawise-store';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Play, Square, Pause, Clock, Droplets, Loader2, ChevronDown } from 'lucide-react';

const statusConfig: Record<string, { color: string; label: string; icon: React.ElementType }> = {
  running: { color: 'bg-blue-500', label: 'Running', icon: Droplets },
  idle: { color: 'bg-gray-400', label: 'Idle', icon: Clock },
  suspended: { color: 'bg-yellow-500', label: 'Suspended', icon: Pause },
};

const runDurations = [
  { label: '5 min', seconds: 300 },
  { label: '10 min', seconds: 600 },
  { label: '15 min', seconds: 900 },
  { label: '30 min', seconds: 1800 },
  { label: '1 hour', seconds: 3600 },
];

const suspendDurations = [
  { label: '1 hour', seconds: 3600 },
  { label: '2 hours', seconds: 7200 },
  { label: '4 hours', seconds: 14400 },
  { label: '8 hours', seconds: 28800 },
  { label: '24 hours', seconds: 86400 },
];

interface ZoneCardProps {
  zone: HydrawiseZone;
}

export const ZoneCard: React.FC<ZoneCardProps> = ({ zone }) => {
  const { runZone, stopZone, suspendZone, isControlling } = useHydrawiseStore();
  const config = statusConfig[zone.status] || statusConfig.idle;
  const StatusIcon = config.icon;

  const formatTimeRemaining = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}h ${remainMins}m`;
    }
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <Card className="relative">
      <CardContent className="p-4 space-y-3">
        {/* Zone header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${config.color}`} />
            <span className="font-medium text-sm">{zone.name}</span>
          </div>
          <span className="text-xs text-muted-foreground">#{zone.relayNumber}</span>
        </div>

        {/* Status info */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <StatusIcon className="h-3 w-3" />
          <span>{config.label}</span>
          {zone.status === 'running' && zone.timeRemaining && (
            <span className="ml-1">· {formatTimeRemaining(zone.timeRemaining)} left</span>
          )}
        </div>

        {/* Next run */}
        {zone.status !== 'running' && zone.nextRunDescription && (
          <p className="text-xs text-muted-foreground">
            Next: {zone.nextRunDescription}
          </p>
        )}

        {/* Controls */}
        <div className="flex items-center gap-1.5">
          {zone.status === 'running' ? (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={() => stopZone(zone.relayId)}
              disabled={isControlling}
            >
              {isControlling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Square className="h-3 w-3" />}
              <span className="ml-1">Stop</span>
            </Button>
          ) : (
            <>
              {/* Run dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-1 text-xs" disabled={isControlling}>
                    <Play className="h-3 w-3" />
                    <span className="ml-1">Run</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {runDurations.map((d) => (
                    <DropdownMenuItem key={d.seconds} onClick={() => runZone(zone.relayId, d.seconds)}>
                      {d.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Suspend dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs" disabled={isControlling}>
                    <Pause className="h-3 w-3" />
                    <ChevronDown className="h-3 w-3 ml-0.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {suspendDurations.map((d) => (
                    <DropdownMenuItem key={d.seconds} onClick={() => suspendZone(zone.relayId, d.seconds)}>
                      Suspend {d.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
